import { InteractionEvent } from '@logto/schemas';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SecondaryPageLayout from '@/Layout/SecondaryPageLayout';
import { fulfillProfile, fulfillProfileWithoutSubmit } from '@/apis/experience';
import SetPasswordForm from '@/containers/SetPassword';
import useApi from '@/hooks/use-api';
import { usePromiseConfirmModal } from '@/hooks/use-confirm-modal';
import type { ErrorHandlers } from '@/hooks/use-error-handler';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import usePasswordPolicyChecker from '@/hooks/use-password-policy-checker';
import usePasswordRejectionErrorHandler from '@/hooks/use-password-rejection-handler';
import { usePasswordPolicy } from '@/hooks/use-sie';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';
import { type ContinueFlowInteractionEvent } from '@/types';
import { trackEvent } from '@/utils/analytics';

type Props = {
  readonly interactionEvent: ContinueFlowInteractionEvent;
};

const SetPassword = ({ interactionEvent }: Props) => {
  const [errorMessage, setErrorMessage] = useState<string>();

  const clearErrorMessage = useCallback(() => {
    setErrorMessage(undefined);
  }, []);

  const navigate = useNavigate();
  const { show } = usePromiseConfirmModal();
  const redirectTo = useGlobalRedirectTo();

  const checkPassword = usePasswordPolicyChecker({ setErrorMessage });
  const addPassword = useApi(fulfillProfileWithoutSubmit);
  const submitPassword = useApi(fulfillProfile);
  const handleError = useErrorHandler();

  const passwordRejectionErrorHandler = usePasswordRejectionErrorHandler({ setErrorMessage });
  const submitInteractionErrorHandler = useSubmitInteractionErrorHandler(interactionEvent, {
    replace: true,
  });

  const errorHandlers: ErrorHandlers = useMemo(
    () => ({
      'user.password_exists_in_profile': async (error) => {
        await show({ type: 'alert', ModalContent: error.message, cancelText: 'action.got_it' });
        navigate(-1);
      },
      ...submitInteractionErrorHandler,
      ...passwordRejectionErrorHandler,
    }),
    [navigate, passwordRejectionErrorHandler, submitInteractionErrorHandler, show]
  );

  const onSubmitHandler = useCallback(
    async (password: string) => {
      const success = await checkPassword(password);

      if (!success) {
        return;
      }

      // SignIn continue flow (existing account completing a missing password):
      // submit the interaction directly so the user signs in to the existing
      // account with their data intact. Only Register defers user creation
      // to the extra-profile page.
      if (interactionEvent !== InteractionEvent.Register) {
        const [error, result] = await submitPassword(
          { type: 'password', value: password },
          interactionEvent
        );

        if (error) {
          await handleError(error, errorHandlers);
          return;
        }

        trackEvent(
          'set_password_continue',
          'submitted',
          { eventId: 'continue_set_password_continue_submitted' },
          {}
        );

        if (result?.redirectTo) {
          await redirectTo(result.redirectTo);
        }

        return;
      }

      // Password is staged in the interaction session; the user is created
      // on the extra-profile page together with submitInteraction
      const [error] = await addPassword({ type: 'password', value: password });

      if (error) {
        await handleError(error, errorHandlers);
        return;
      }

      trackEvent(
        'set_password_continue',
        'submitted',
        { eventId: 'continue_set_password_continue_submitted' },
        {}
      );

      // Navigate to extra-profile instead of redirecting
      navigate('/continue/extra-profile', {
        replace: true,
        state: { interactionEvent, isSocialLogin: false },
      });
    },
    [
      addPassword,
      submitPassword,
      checkPassword,
      errorHandlers,
      handleError,
      interactionEvent,
      navigate,
      redirectTo,
    ]
  );

  const {
    policy: {
      length: { min, max },
      characterTypes: { min: count },
    },
    requirementsDescription,
  } = usePasswordPolicy();

  return (
    <SecondaryPageLayout
      title="description.set_password"
      description={requirementsDescription && <span>{requirementsDescription}</span>}
      descriptionProps={{ min, count }}
    >
      <SetPasswordForm
        autoFocus
        errorMessage={errorMessage}
        maxLength={max}
        clearErrorMessage={clearErrorMessage}
        onSubmit={onSubmitHandler}
      />
    </SecondaryPageLayout>
  );
};

export default SetPassword;
